import dateUtil from '@/js/utils/dateUtil'

/**
 * When signup for an event closes, and whether it already has.
 *
 * The rule was copy-pasted into the public join form and the event repository, which is how two
 * surfaces end up disagreeing about whether a class is still open. It is a business rule, so it
 * lives here and both call it.
 */

/**
 * Grace period when a class sets no signup-close window: signup stays open until shortly after the
 * class has started, rather than closing exactly on the start time.
 *
 * Mirrors `AppConstants.EVENT_BOOKING_START_GRACE_MINUTES` in `event-api` — the two must stay
 * equal, or the list shows a class as open while the booking endpoint refuses it. The backend
 * applies the same rule in `REventParticipantService.getSignupCloseTime`.
 */
export const DEFAULT_SIGNUP_GRACE_MINUTES = 5

/**
 * No timezone appears here, and none is needed:
 * - `startTime` arrives as ISO-8601 **with `Z`** (the API sends a Java `Instant`), so `dateUtil.toInstant`
 *   parses a fixed point in time rather than guessing a zone. A zone-less string such as
 *   `2026-09-25T10:30:00` would be read as the *viewer's* local time — if the API ever starts
 *   sending that, this is the line that breaks.
 * - Adding minutes is a duration, not a calendar operation, so it means the same everywhere. It
 *   goes through `dateUtil.addMinutes` only to avoid `setMinutes`, which does the arithmetic on
 *   local clock fields and drifts by an hour across a daylight-saving change.
 *
 * A zone would only be needed to ask which *day* or *hour* the close time falls in.
 *
 * @param {string|Date} startTime event start (an instant from the API)
 * @param {number|null|undefined} closeSignupBeforeMinutes minutes before start that signup closes;
 *   0, null or missing means "no window configured"
 * @returns {Date} the moment signup closes
 */
export function getSignupCloseTime (startTime, closeSignupBeforeMinutes) {
  const start = dateUtil.toInstant(startTime)
  if (closeSignupBeforeMinutes > 0) {
    return dateUtil.addMinutes(start, -closeSignupBeforeMinutes)
  }
  return dateUtil.addMinutes(start, DEFAULT_SIGNUP_GRACE_MINUTES)
}

/**
 * @returns {boolean} true when signup for this event is already closed.
 */
export function isSignupClosed (startTime, closeSignupBeforeMinutes) {
  return getSignupCloseTime(startTime, closeSignupBeforeMinutes) <= dateUtil.getNow()
}

/**
 * Same question, for the surfaces the API already answers it for: list and calendar endpoints send
 * a computed `close_signup_time`, so they compare that instead of recomputing the window.
 *
 * No timezone is involved on purpose. `closeSignupTime` is an instant (ISO-8601 with `Z`) and so is
 * now, and "has this moment passed?" has the same answer in every timezone. A zone would only be
 * needed to ask which *day* or *hour* an instant falls in.
 *
 * @param {string|Date|null|undefined} closeSignupTime
 * @returns {boolean} false when the event carries no close time.
 *
 * A missing close time means **open**, deliberately: the public endpoints always compute one
 * (`RClassPublicService` sends the effective value), so a null here means the field was not part
 * of that response, not that the class is past. Treating it as closed would hide bookable classes
 * from any surface that omits the field, which is the worse failure of the two — a booking that
 * should not have been offered is refused by the API, with its own error.
 */
export function isSignupClosedAt (closeSignupTime) {
  if (!closeSignupTime) {
    return false
  }
  return dateUtil.toInstant(closeSignupTime) <= dateUtil.getNow()
}
